import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { Card } from "../../components/common/Card";
import { Badge } from "../../components/common/Badge";
import { DataTable, Column } from "../../components/common/Table";
import { Modal } from "../../components/common/Modal";
import { useAuth } from "../../context/AuthContext";
import type { Asset, Plant, Unit } from "../../types";

interface MaintenanceRequest {
  id: number;
  request_number: string;
  plant_id: number | null;
  unit_id: number | null;
  asset_id: number | null;
  plant_name: string | null;
  unit_name: string | null;
  asset_name: string | null;
  tag_no: string | null;
  priority: string;
  status: string;
  description: string;
  requested_by_name: string;
  request_date: string;
}

export default function MaintenanceRequests() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState<MaintenanceRequest[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [convertTarget, setConvertTarget] = useState<MaintenanceRequest | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await api.get<MaintenanceRequest[]>("/api/maintenance-requests"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  const loadLookups = async () => {
    const [p, u] = await Promise.all([api.get<Plant[]>("/api/master-data/plants"), api.get<Unit[]>("/api/master-data/units")]);
    setPlants(p);
    setUnits(u);
  };

  useEffect(() => {
    load();
    loadLookups();
  }, []);

  const convert = async (r: MaintenanceRequest) => {
    if (!r.asset_id) {
      // No Equipment on the original request - ask which one this Work Order is for.
      setConvertTarget(r);
      return;
    }
    await api.post(`/api/maintenance-requests/${r.id}/convert`);
    load();
  };

  const columns: Column<MaintenanceRequest>[] = [
    { header: "Request #", accessor: (r) => <span className="font-mono text-xs">{r.request_number}</span> },
    { header: "Plant", accessor: (r) => r.plant_name ?? "-" },
    { header: "Unit", accessor: (r) => r.unit_name ?? "-" },
    { header: "Equipment", accessor: (r) => (r.asset_id ? r.tag_no ?? r.asset_name : <span className="text-slate-400">(none)</span>) },
    { header: "Requested By", accessor: (r) => r.requested_by_name },
    { header: "Priority", accessor: (r) => <Badge value={r.priority} /> },
    { header: "Status", accessor: (r) => <Badge value={r.status} /> },
    {
      header: "Action",
      accessor: (r) =>
        r.status === "SUBMITTED" && hasPermission("*", "wo:create") ? (
          <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => convert(r)}>
            Convert to WO
          </button>
        ) : (
          "-"
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Maintenance Requests</h1>
          <p className="text-sm text-slate-500">Production submits → Supervisor reviews → converts to Work Order.</p>
        </div>
        {hasPermission("*", "request:create") && (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New Request
          </button>
        )}
      </div>
      <Card>
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
        {loading ? <p className="text-sm text-slate-400">Loading…</p> : <DataTable columns={columns} rows={rows} keyField={(r) => r.id} />}
      </Card>
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Maintenance Request">
        <RequestForm
          plants={plants}
          units={units}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      </Modal>

      <Modal open={!!convertTarget} onClose={() => setConvertTarget(null)} title="Select Equipment for Work Order">
        {convertTarget && (
          <ConvertPickEquipmentForm
            request={convertTarget}
            onConverted={() => {
              setConvertTarget(null);
              load();
            }}
          />
        )}
      </Modal>
    </div>
  );
}

function RequestForm({ plants, units, onCreated }: { plants: Plant[]; units: Unit[]; onCreated: () => void }) {
  const [form, setForm] = useState({ plantId: "", unitId: "", assetId: "", breakdownCategory: "CORRECTIVE", priority: "MEDIUM", description: "" });
  const [equipment, setEquipment] = useState<Asset[]>([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const unitsForPlant = useMemo(() => (form.plantId ? units.filter((u) => u.plant_id === Number(form.plantId)) : []), [units, form.plantId]);

  useEffect(() => {
    if (!form.unitId) {
      setEquipment([]);
      return;
    }
    setLoadingEquipment(true);
    api
      .get<Asset[]>(`/api/assets?unitId=${form.unitId}`)
      .then(setEquipment)
      .catch(() => setEquipment([]))
      .finally(() => setLoadingEquipment(false));
  }, [form.unitId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.plantId || !form.unitId) {
      setError("Plant and Unit are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/maintenance-requests", {
        plantId: Number(form.plantId),
        unitId: Number(form.unitId),
        assetId: form.assetId ? Number(form.assetId) : undefined,
        breakdownCategory: form.breakdownCategory,
        priority: form.priority,
        description: form.description,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Plant</label>
          <select
            className="input-field"
            required
            value={form.plantId}
            onChange={(e) => setForm({ ...form, plantId: e.target.value, unitId: "", assetId: "" })}
          >
            <option value="">Select Plant</option>
            {plants.map((p) => (
              <option key={p.id} value={p.id}>
                {p.plant_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Unit</label>
          <select
            className="input-field"
            required
            disabled={!form.plantId}
            value={form.unitId}
            onChange={(e) => setForm({ ...form, unitId: e.target.value, assetId: "" })}
          >
            <option value="">Select Unit</option>
            {unitsForPlant.map((u) => (
              <option key={u.id} value={u.id}>
                {u.unit_code} - {u.unit_name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500">
          Equipment <span className="text-slate-400">(optional - leave blank if the whole unit/area is affected)</span>
        </label>
        <select className="input-field" disabled={!form.unitId} value={form.assetId} onChange={(e) => setForm({ ...form, assetId: e.target.value })}>
          <option value="">{loadingEquipment ? "Loading…" : "No specific equipment"}</option>
          {equipment.map((a) => (
            <option key={a.id} value={a.id}>
              {a.tag_no ?? a.asset_code} - {a.asset_name}
            </option>
          ))}
        </select>
      </div>
      <select className="input-field" value={form.breakdownCategory} onChange={(e) => setForm({ ...form, breakdownCategory: e.target.value })}>
        <option value="PREVENTIVE">Preventive</option>
        <option value="CORRECTIVE">Corrective</option>
        <option value="IMPROVEMENT">Improvement</option>
      </select>
      <select className="input-field" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
        <option value="CRITICAL">Critical</option>
        <option value="HIGH">High</option>
        <option value="MEDIUM">Medium</option>
        <option value="LOW">Low</option>
      </select>
      <textarea className="input-field" placeholder="Describe the problem…" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      <button type="submit" disabled={submitting} className="btn-primary w-full">
        {submitting ? "Submitting…" : "Submit Request"}
      </button>
    </form>
  );
}

function ConvertPickEquipmentForm({ request, onConverted }: { request: MaintenanceRequest; onConverted: () => void }) {
  const [equipment, setEquipment] = useState<Asset[]>([]);
  const [assetId, setAssetId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!request.unit_id) return;
    api
      .get<Asset[]>(`/api/assets?unitId=${request.unit_id}`)
      .then(setEquipment)
      .catch(() => setEquipment([]));
  }, [request.unit_id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assetId) {
      setError("Choose the equipment this Work Order is for");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/api/maintenance-requests/${request.id}/convert`, { assetId: Number(assetId) });
      onConverted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to convert request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-slate-500">
        Request <span className="font-mono">{request.request_number}</span> ({request.plant_name} / {request.unit_name}) didn't specify a piece of equipment. A Work Order needs one.
      </p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <select className="input-field" required value={assetId} onChange={(e) => setAssetId(e.target.value)}>
        <option value="">Select Equipment</option>
        {equipment.map((a) => (
          <option key={a.id} value={a.id}>
            {a.tag_no ?? a.asset_code} - {a.asset_name}
          </option>
        ))}
      </select>
      <button type="submit" disabled={submitting} className="btn-primary w-full">
        {submitting ? "Converting…" : "Convert to Work Order"}
      </button>
    </form>
  );
}
